import React from 'react';
import { HomeIcon, MapPinIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { labPanels } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
const markerGroups = [{
  group: 'Hormones',
  markers: ['Total testosterone', 'Free testosterone', 'Estradiol', 'SHBG', 'LH', 'FSH', 'DHEA-S', 'Progesterone']
}, {
  group: 'Metabolic',
  markers: ['HbA1c', 'Fasting glucose', 'Fasting insulin', 'ApoB', 'Lp(a)', 'Full lipid panel', 'Uric acid']
}, {
  group: 'Thyroid & inflammation',
  markers: ['TSH', 'Free T3', 'Free T4', 'TPO antibodies', 'hs-CRP', 'Homocysteine', 'Ferritin']
}, {
  group: 'Organ & blood',
  markers: ['CBC with differential', 'CMP', 'eGFR', 'ALT / AST', 'IGF-1', 'Vitamin D', 'B12']
}];
export function Labs() {
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Labs — ${brand.name}`,
    description: 'A 62-marker blood panel included with every membership. Do it at home, or at any of 2,100 draw sites.'
  });
  return <main data-id="element-484" data-mp="x8jtjnb0ejj1">
      <PageHeader eyebrow="Labs" title="Your bloodwork is included, not an upsell." intro="Every membership comes with a full panel, and a doctor writes it up in language you can actually use — before anyone suggests a treatment." data-id="element-485" data-mp="x9uwjkwmrn93" />

      <section className="bg-cream" data-id="element-486" data-mp="9j8rm2hxaj3b">
        <div className="mx-auto max-w-shell px-6 pb-16 md:px-8 md:pb-20" data-id="element-487" data-mp="9ka2a5v113ki">
          <img src={brand.images.labKit} alt={`A ${brand.name} at-home blood test kit`} className="aspect-[16/7] w-full rounded-brand object-cover" data-id="element-488" data-mp="efpd0oxqmjcp" />

          <ul className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-489" data-mp="1pvw4t1bpz4x1">
            {labPanels.map(panel => <li key={panel.name} className="rounded-brand border border-ink/10 bg-cream-light p-6" data-id="element-490" data-mp="1twg2c607gajp">
                <div className="flex items-baseline justify-between gap-4" data-id="element-491" data-mp="3qx3tz20tsuf5">
                  <h2 className="font-serif text-xl font-semibold text-ink" data-id="element-492" data-mp="3lyirsx20k0o2">{panel.name}</h2>
                  <span className="font-serif text-3xl font-bold text-brick" data-id="element-493" data-mp="3sw9ua197qgh">{panel.markers}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft" data-id="element-494" data-mp="3v0rzlf5wdfxr">{panel.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-495" data-mp="339m45c14g6et">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-496" data-mp="33ax8pe2e5i6z">
          <SectionHeading eyebrow="What we test" title="Here is the whole list, before you book." intro="If a marker is on your panel, your doctor can tell you why it is there and what your number means." data-id="element-497" data-mp="3h193xlu9vx2c" />
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4" data-id="element-498" data-mp="2jl3aah5684ts">
            {markerGroups.map(group => <div key={group.group} className="rounded-brand border border-ink/10 bg-cream p-6" data-id="element-499" data-mp="z2j150yms7me">
                <h3 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-500" data-mp="13uax5r0k35jx">
                  {group.group}
                </h3>
                <ul className="mt-4 flex flex-col gap-2.5" data-id="element-501" data-mp="q47etb4srvzr">
                  {group.markers.map(marker => <li key={marker} className="text-sm text-ink-soft" data-id="element-502" data-mp="uzczy7qdl6ms">
                      {marker}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-503" data-mp="18tng40ob3sh8">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-504" data-mp="18sm4nv3dp3yz">
          <SectionHeading eyebrow="Getting tested" title="Two ways to do it." data-id="element-505" data-mp="1dqw2yekegkot" />
          <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-506" data-mp="3tijvlxsdrr1t">
            {[{
            icon: HomeIcon,
            title: 'At your kitchen table',
            detail: 'The kit ships the next business day. Ten minutes to collect, then drop it back in the prepaid mailer. Results in three to five days.',
            meta: 'Included with membership'
          }, {
            icon: MapPinIcon,
            title: 'At a draw site near you',
            detail: 'Book at any of 2,100 locations, usually the same week. Early fasting appointments before 10am if you need them.',
            meta: 'Included with membership'
          }].map(option => <div key={option.title} className="rounded-brand border border-ink/10 bg-cream-light p-8" data-id="element-507" data-mp="rkmjh66qvj42">
                <option.icon className="h-6 w-6 text-brick" strokeWidth={1.6} data-id="element-508" data-mp="mkqep4q83nng" />
                <h3 className="mt-5 font-serif text-2xl font-semibold text-ink" data-id="element-509" data-mp="3cb3qek1u6kia">{option.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-510" data-mp="37bhd6am0d49h">{option.detail}</p>
                <p className="mt-5 text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-511" data-mp="2x8llmp4929v3">
                  {option.meta}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <CtaBand title="Get the numbers first." intro="You can buy a panel on its own, no membership, if you would rather see where you stand before committing to anything." data-id="element-512" data-mp="37585bakuauvj" />
    </main>;
}